const PAGING_KEYS = ["PageUp", "PageDown"];

const NON_TEXT_INPUT_TYPES = ["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"];

export const isEditableField = (target: EventTarget | null): target is HTMLElement => {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.includes(target.type);
  return target.isContentEditable === true;
};

/**
 * PageUp/PageDown in a text field moves its caret by a page, and the browser then scrolls every overflowing
 * ancestor to keep that caret in view. That includes the `overflow: hidden` app shell, which overflows the
 * window slightly and so can be scrolled by script but never by the user: the page jumps up, the toolbar with
 * the Save button leaves the screen, and nothing brings it back short of a reload.
 *
 * Leave the key's default alone, so the field itself still scrolls and its caret still moves, and put every
 * ancestor back where it was before the next paint. Returns the function that removes the listener.
 *
 * Only the browser's own scroll is undone: a handler further down the tree that cancels the key has taken
 * over paging, and whatever it scrolled is deliberate. While a restore is pending, a repeated keydown keeps
 * the first snapshot, because by then the ancestors are already scrolled and a new one would restore to that.
 */
export const containFieldPaging = (
  doc: Document,
  schedule: (restore: () => void) => void = (restore) => window.requestAnimationFrame(restore)
): (() => void) => {
  let pending = false;

  const onKeyDown = (event: KeyboardEvent) => {
    if (pending || !PAGING_KEYS.includes(event.key) || event.defaultPrevented || !isEditableField(event.target)) return;

    const positions: Array<[Element, number, number]> = [];
    for (let el = event.target.parentElement; el; el = el.parentElement)
      positions.push([el, el.scrollTop, el.scrollLeft]);

    pending = true;
    schedule(() => {
      pending = false;
      if (event.defaultPrevented) return;
      for (const [el, top, left] of positions) {
        if (el.scrollTop !== top) el.scrollTop = top;
        if (el.scrollLeft !== left) el.scrollLeft = left;
      }
    });
  };

  doc.addEventListener("keydown", onKeyDown, true);
  return () => doc.removeEventListener("keydown", onKeyDown, true);
};
